import {
  useCallback, useEffect, useId, useRef, useState, type KeyboardEvent,
} from 'react';
import './listas.css';

/**
 * O período — um botão, um calendário só (onda 270).
 *
 * «filtro de data (apenas 1 calendário que escolhe a data inicial e final)»
 * — Humberto, 03/10/2026, no pedido do padrão de listas.
 *
 * ── Por que um mês, e não dois ────────────────────────────────────────
 *
 * A lista de OS tinha dois campos digitados e, ao lado, um calendário de
 * DOIS meses (31/08). Funcionava, mas eram três jeitos de dizer a mesma
 * coisa na mesma faixa, e cada lista que precisasse de data inventaria o
 * seu (a Assinatura inventou dois campos soltos). O padrão Carbon
 * Testesolo fecha num componente só: um botão na barra de ferramentas que
 * diz o período em texto curto, e que abre UM mês. O primeiro clique marca
 * o início, o segundo o fim. Quem precisa cruzar a virada do mês anda com
 * ‹ › — o início escolhido fica guardado e aparece no campo «De».
 *
 * ── Rascunho, Aplicar e Cancelar ──────────────────────────────────────
 *
 * Cada clique no calendário mexe num RASCUNHO; a lista só muda no
 * «Aplicar». Sem isso, o primeiro clique (só o início) já filtraria a lista
 * pela metade do pedido, e a tabela piscaria a cada dia escolhido.
 *
 * ── O primeiro clique com o intervalo completo sempre recomeça ─────────
 *
 * A regra do calendário da OS (31/08), que continua valendo: com as duas
 * datas escolhidas, clicar de novo é começar de novo. Clicar antes do
 * início não é erro: o par se inverte, que é o que a pessoa quis dizer.
 */

export interface ValorDoPeriodo {
  /** `YYYY-MM-DD`, ou vazio. */
  de: string;
  ate: string;
  /** A chave do atalho que gerou o período — o botão diz o nome dele. */
  atalho?: string | null;
  /** Só nas listas que oferecem «incluir o que ficou para trás». */
  pendentes?: boolean;
}

export interface Atalho {
  chave: string;
  texto: string;
  /** Devolve [de, ate] a partir de hoje (`YYYY-MM-DD`). Vazio = sem limite. */
  calcular: (hoje: string) => [string, string];
}

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const SEMANA_CURTA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const SEMANA_LETRA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

/** Hoje no fuso de São Paulo, como a API conta o dia (0189). */
export function hojeIso(): string {
  try {
    return new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
  } catch {
    const d = new Date();
    return isoDe(d.getFullYear(), d.getMonth(), d.getDate());
  }
}

const isoDe = (ano: number, mes: number, dia: number) =>
  `${ano}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;

/** Soma dias a uma data `YYYY-MM-DD` sem passar pelo fuso. */
export function somarDias(iso: string, n: number): string {
  const [a, m, d] = iso.split('-').map(Number);
  const t = new Date(Date.UTC(a, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}

/** Dias corridos de `a` até `b` (positivo se `b` vem depois). */
export function diasEntre(a: string, b: string): number {
  const [a1, a2, a3] = a.split('-').map(Number);
  const [b1, b2, b3] = b.split('-').map(Number);
  return Math.round((Date.UTC(b1, b2 - 1, b3) - Date.UTC(a1, a2 - 1, a3)) / 86400000);
}

export function diaDaSemana(iso: string): string {
  const [a, m, d] = iso.split('-').map(Number);
  return SEMANA_CURTA[new Date(Date.UTC(a, m - 1, d)).getUTCDay()];
}

/** `03/10` */
export const diaMes = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
/** `03/10/2026` */
export const diaMesAno = (iso: string) => `${diaMes(iso)}/${iso.slice(0, 4)}`;

const inicioDaSemana = (hoje: string) => {
  const [a, m, d] = hoje.split('-').map(Number);
  const dow = new Date(Date.UTC(a, m - 1, d)).getUTCDay();
  return somarDias(hoje, -((dow + 6) % 7));
};
const inicioDoMes = (hoje: string) => `${hoje.slice(0, 8)}01`;
const fimDoMes = (hoje: string) => {
  const [a, m] = hoje.split('-').map(Number);
  return isoDe(a, m - 1, new Date(Date.UTC(a, m, 0)).getUTCDate());
};

/** O catálogo de atalhos. Cada lista escolhe os que fazem sentido para ela. */
export const ATALHOS: Record<string, Atalho> = {
  hoje: { chave: 'hoje', texto: 'Hoje', calcular: (h) => [h, h] },
  proximos7: { chave: 'proximos7', texto: 'Próximos 7 dias', calcular: (h) => [h, somarDias(h, 7)] },
  semana: { chave: 'semana', texto: 'Esta semana', calcular: (h) => [inicioDaSemana(h), somarDias(inicioDaSemana(h), 6)] },
  mes: { chave: 'mes', texto: 'Este mês', calcular: (h) => [inicioDoMes(h), fimDoMes(h)] },
  mesPassado: {
    chave: 'mesPassado', texto: 'Mês passado',
    calcular: (h) => { const f = somarDias(inicioDoMes(h), -1); return [inicioDoMes(f), f]; },
  },
  ultimos7: { chave: 'ultimos7', texto: 'Últimos 7 dias', calcular: (h) => [somarDias(h, -7), h] },
  ultimos30: { chave: 'ultimos30', texto: 'Últimos 30 dias', calcular: (h) => [somarDias(h, -30), h] },
  tudo: { chave: 'tudo', texto: 'Sem limite de data', calcular: () => ['', ''] },
};

/** O texto curto do botão: «Próximos 7 dias · 03/10–10/10», «01/10 – 31/10/2026». */
export function textoDoPeriodo(v: ValorDoPeriodo): string {
  const at = v.atalho ? ATALHOS[v.atalho] : null;
  if (!v.de && !v.ate) return 'Sem limite';
  const faixa = v.de && v.ate
    ? (v.de === v.ate ? diaMesAno(v.de) : `${diaMes(v.de)} – ${diaMesAno(v.ate)}`)
    : v.de ? `a partir de ${diaMesAno(v.de)}` : `até ${diaMesAno(v.ate)}`;
  return at && at.chave !== 'tudo' ? `${at.texto} · ${faixa}` : faixa;
}

/** O período de um atalho, calculado agora. */
export function periodoDoAtalho(chave: string, extra: Partial<ValorDoPeriodo> = {}): ValorDoPeriodo {
  const [de, ate] = ATALHOS[chave].calcular(hojeIso());
  return { de, ate, atalho: chave, ...extra };
}

export function Periodo({
  valor, aoMudar, rotulo = 'Período', atalhos = ['hoje', 'semana', 'mes', 'mesPassado', 'ultimos30', 'tudo'],
  campos, campo, aoMudarCampo, rotuloPendentes,
}: {
  valor: ValorDoPeriodo;
  aoMudar(v: ValorDoPeriodo): void;
  rotulo?: string;
  atalhos?: string[];
  /** Quando a lista tem mais de uma data (OS: abertura, prazo…). */
  campos?: { chave: string; texto: string }[];
  campo?: string;
  aoMudarCampo?(c: string): void;
  /** Quando existe, o calendário oferece «incluir o que ficou para trás». */
  rotuloPendentes?: string;
}) {
  const [aberto, setAberto] = useState(false);
  const [rascunho, setRascunho] = useState<ValorDoPeriodo>(valor);
  const [rascunhoCampo, setRascunhoCampo] = useState(campo);
  const hoje = hojeIso();
  const [base, setBase] = useState(() => (valor.de || hoje).slice(0, 7));
  const caixa = useRef<HTMLDivElement>(null);
  const grade = useRef<HTMLDivElement>(null);
  const id = useId();

  const abrir = () => {
    setRascunho(valor);
    setRascunhoCampo(campo);
    setBase((valor.de || hoje).slice(0, 7));
    setAberto(true);
  };
  const fechar = useCallback(() => setAberto(false), []);

  /* Clicar fora fecha sem aplicar — é o «Cancelar» de quem desistiu. */
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: PointerEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) fechar();
    };
    document.addEventListener('pointerdown', fora);
    return () => document.removeEventListener('pointerdown', fora);
  }, [aberto, fechar]);

  const aplicar = () => {
    const de = rascunho.de;
    const ate = rascunho.ate || rascunho.de;
    aoMudar({ ...rascunho, de, ate });
    if (rascunhoCampo !== undefined && rascunhoCampo !== campo) aoMudarCampo?.(rascunhoCampo);
    fechar();
  };

  const escolher = (dia: string) => setRascunho((r) => {
    if (!r.de || (r.de && r.ate)) return { ...r, de: dia, ate: '', atalho: null };
    if (dia < r.de) return { ...r, de: dia, ate: r.de, atalho: null };
    return { ...r, ate: dia, atalho: null };
  });

  const [ano, mes] = base.split('-').map(Number);
  const vazios = new Date(Date.UTC(ano, mes - 1, 1)).getUTCDay();
  const dias = new Date(Date.UTC(ano, mes, 0)).getUTCDate();
  const andar = (n: number) => {
    const t = new Date(Date.UTC(ano, mes - 1 + n, 1));
    setBase(t.toISOString().slice(0, 7));
  };

  /*
   * As setas andam pelos dias, como num calendário do Carbon. Ao passar da
   * borda do mês, o mês vira e o foco vai para o dia certo no outro.
   */
  const teclar = (e: KeyboardEvent<HTMLDivElement>) => {
    const passos: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
    const p = passos[e.key];
    const atual = (e.target as HTMLElement).dataset?.dia;
    if (!p || !atual) return;
    e.preventDefault();
    const alvo = somarDias(atual, p);
    if (alvo.slice(0, 7) !== base) setBase(alvo.slice(0, 7));
    requestAnimationFrame(() => {
      grade.current?.querySelector<HTMLButtonElement>(`button[data-dia="${alvo}"]`)?.focus();
    });
  };

  const ativo = !!(valor.de || valor.ate);
  const r = rascunho;

  return (
    <div className="periodo-barra" ref={caixa}
      onKeyDown={(e) => { if (e.key === 'Escape' && aberto) { e.stopPropagation(); fechar(); } }}>
      <button type="button" className={`barra-filtro-botao${ativo ? ' ativo' : ''}`}
        aria-expanded={aberto} aria-controls={id} onClick={() => (aberto ? fechar() : abrir())}>
        <span className="rot">{campos && campo
          ? `${rotulo} · ${campos.find((c) => c.chave === campo)?.texto ?? ''}`
          : rotulo}</span>
        <span className="val">{textoDoPeriodo(valor)}</span>
      </button>

      {aberto && (
        <div className="periodo-caixa" id={id} role="dialog" aria-label={`Escolher ${rotulo.toLowerCase()}`}>
          <div className="periodo-atalhos">
            {campos && (
              <label className="periodo-campo">
                <span>Data de</span>
                <select value={rascunhoCampo} onChange={(e) => setRascunhoCampo(e.target.value)}>
                  {campos.map((c) => <option key={c.chave} value={c.chave}>{c.texto}</option>)}
                </select>
              </label>
            )}
            {atalhos.map((k) => {
              const a = ATALHOS[k];
              if (!a) return null;
              return (
                <button key={k} type="button" aria-pressed={r.atalho === k}
                  className={r.atalho === k ? 'aqui' : ''}
                  onClick={() => {
                    const [de, ate] = a.calcular(hoje);
                    setRascunho({ ...r, de, ate, atalho: k });
                    if (de) setBase(de.slice(0, 7));
                  }}>{a.texto}</button>
              );
            })}
          </div>
          <div className="periodo-corpo">
            <div className="periodo-de-ate">
              <div><span>De</span><b>{r.de ? `${diaDaSemana(r.de)}, ${diaMesAno(r.de)}` : '—'}</b></div>
              <div><span>Até</span><b>{r.ate
                ? `${diaDaSemana(r.ate)}, ${diaMesAno(r.ate)}`
                : r.de ? 'clique no último dia' : '—'}</b></div>
            </div>
            <div className="periodo-mes">
              <button type="button" onClick={() => andar(-1)} aria-label="Mês anterior">‹</button>
              <b>{MESES[mes - 1]} de {ano}</b>
              <button type="button" onClick={() => andar(1)} aria-label="Próximo mês">›</button>
            </div>
            <div className="periodo-semana" aria-hidden="true">
              {SEMANA_LETRA.map((d, i) => <span key={i}>{d}</span>)}
            </div>
            <div className="periodo-dias" ref={grade} onKeyDown={teclar}>
              {Array.from({ length: vazios }, (_, i) => <span key={`v${i}`} />)}
              {Array.from({ length: dias }, (_, i) => {
                const dia = isoDe(ano, mes - 1, i + 1);
                const ponta = dia === r.de || dia === r.ate;
                const dentro = !!r.de && !!r.ate && dia > r.de && dia < r.ate;
                const dow = (vazios + i) % 7;
                return (
                  <button key={dia} type="button" data-dia={dia}
                    className={[
                      'periodo-dia',
                      ponta ? 'ponta' : '',
                      dentro ? 'dentro' : '',
                      dia === hoje ? 'hoje' : '',
                      dow === 0 || dow === 6 ? 'fds' : '',
                    ].filter(Boolean).join(' ')}
                    aria-pressed={ponta}
                    aria-label={`${i + 1} de ${MESES[mes - 1]} de ${ano}${dia === hoje ? ', hoje' : ''}`}
                    onClick={() => escolher(dia)}>
                    {i + 1}
                  </button>
                );
              })}
            </div>
            <p className="periodo-dica">
              {!r.de || (r.de && r.ate)
                ? 'Clique no primeiro dia e depois no último.'
                : 'Agora clique no último dia do período.'}
            </p>
            {rotuloPendentes && (
              <label className="periodo-pendentes">
                <input type="checkbox" checked={!!r.pendentes}
                  onChange={(e) => setRascunho({ ...r, pendentes: e.target.checked })} />
                <span>{rotuloPendentes}</span>
              </label>
            )}
            <div className="periodo-pe">
              <button type="button" className="botao discreto" onClick={fechar}>Cancelar</button>
              <button type="button" className="botao" onClick={aplicar}>Aplicar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
